import React, {useState} from 'react';
import config from '../../service/config';
import CopyMediaModal from '../modals/CopyMediaModal';
import ResetDataModal from '../modals/ResetDataModal';
import SetThumbnailModal from '../modals/SetThumbnailModal';
import ReplaceMediaModal from '../modals/ReplaceMediaModal/ReplaceMediaModal';
import {useTranslation} from 'react-i18next';
import DeleteModal from '../modals/DeleteModal';
import {TscPopoverOrigin, Glyphs, MenuItem} from '@techsmith/tsc-cloud-style-guide';
import TestablePopoverButton from '../util/TestablePopoverButton';
import styled from 'styled-components';
import cssConstants from '../../constants/cssConstants';
import {BrowserView} from 'react-device-detect';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {IMediaDetails} from '../../model/mediaDetails';
import Constants from '../../constants/Constants';
import {mediaStore} from '../../context/mediaProvider';
import UserOverQuotaModal from '../modals/UserOverQuotaModal';
import {useUserQuotaStore} from '../../context/userQuotaStore';
import {importMediaInterface} from '../../hooks/dynamicImport';

const MoreMenuWrapper = styled.div`
   display: inline-block;

   @media screen and (min-width: ${cssConstants.contextMenuBreakpoint}px) {
       .more-menu {
         z-index: ${cssConstants.zIndex.viewPageElement};
      }
   }
`;

export const MoreMenuBase: React.FC<IStateMappedProps> = ({Hash, IsYouTube, DownloadUrl, DownloadEnabled, IsReady, IsVideo, Url, Title}) => {
   const [showCopyModal, setShowCopyModal] = useState(false);
   const [showDeleteModal, setShowDeleteModal] = useState(false);
   const [showResetDataModal, setShowResetDataModal] = useState(false);
   const [showSetThumbnailModal, setShowSetThumbnailModal] = useState(false);
   const [showReplaceMedialModal, setShowReplaceMediaModal] = useState(false);
   const refreshUserQuotasFromApi = useUserQuotaStore(s => s.refreshUserQuotasFromApi);

   const {t} = useTranslation();

   const onCopyClick = () => {
      setShowCopyModal(true);
   };

   const onCopyClose = async () => {
      setShowCopyModal(false);
      await refreshUserQuotasFromApi();
   };

   const onDeleteClick = async () => {
      const mediaInterface = await importMediaInterface();
      mediaInterface.currentTime > 0 && !mediaInterface.paused && mediaInterface.pause();
      setShowDeleteModal(true);
   };

   const onDeleteClose = () => {
      setShowDeleteModal(false);
   };

   const onResetDataClick = () => {
      setShowResetDataModal(true);
   };

   const onResetDataClose = () => {
      setShowResetDataModal(false);
   };

   const onSetThumbnailClick = () => {
      setShowSetThumbnailModal(true);
   };

   const onSetThumbnailClose = () => {
      setShowSetThumbnailModal(false);
   };

   const onReplaceMediaClick = () => {
      setShowReplaceMediaModal(true);
   };

   const onReplaceMediaClose = () => {
      setShowReplaceMediaModal(false);
   };

   const canDownload = !IsYouTube && DownloadUrl && (config.watchConfig.isOwnerAdminOrTeamEditor || config.watchConfig.viewersCanDownloadMedia && DownloadEnabled);
   const canCopyMedia = !config.featureSwitches.DisableCopy && !config.environmentData.IsLti && (config.watchConfig.isOwnerAdminOrTeamEditor || config.viewersCanCopyMedia && config.user.IsSignedIn);
   const canUpdateThumbnail = !config.environmentData.IsLti && (config.watchConfig.isOwnerAdminOrTeamEditor) && IsReady && IsVideo && !IsYouTube;
   const canControlMedia = !config.environmentData.IsLti && (config.watchConfig.isOwnerAdminOrTeamEditor);
   const canResetData = !config.featureSwitches.UseSingleConversation && !config.environmentData.IsLti && (config.watchConfig.isOwnerAdminOrTeamEditor);

   const navigateToLibrary = () => {
      window.location.href = Constants.navigation.myLibraryLink;
   };

   const reloadPage = () => {
      window.setTimeout(() => {
         window.location.reload();
      }, 1000);
   };

   const showMenu = canDownload || canCopyMedia || canUpdateThumbnail || canControlMedia;
   const selectedMedia = [{Hash, IsVideo, Title} as IMediaDetails];

   return showMenu && (
      <MoreMenuWrapper>
         <TestablePopoverButton
            buttonGlyph={<Glyphs.EllipsisStacked16x16 />}
            origin={TscPopoverOrigin.topRight}
            showArrow={false}
            testId="more-menu-button"
            buttonText={t('More')}
            buttonTitle={t('More')}
            closeMenuOnClick={true}
            useMobileMenu={true}
            mobileContextMenuZindex={cssConstants.zIndex.mobileContextMenu}
            mobileContextMenuTitle={t('MoreMediaOptions')}
            className="more-menu"
         >
            <>
               {canDownload && (
                  <MenuItem
                     key="download"
                     label={t('Download')}
                     href={DownloadUrl}
                     openInNewTab={true}
                  />
               )}
               {canCopyMedia && (
                  <UserOverQuotaModal>
                     <MenuItem
                        key="copy"
                        label={t('MakeCopy')}
                        onClick={onCopyClick}
                     />
                  </UserOverQuotaModal>
               )}
               {canUpdateThumbnail && (
                  <MenuItem
                     key="thumbnail"
                     label={t('setThumbnail.action')}
                     onClick={onSetThumbnailClick}
                  />
               )}
               {canControlMedia && (
                  <MenuItem
                     key="move-to-trash"
                     label={t('MoveToTrash')}
                     onClick={onDeleteClick}
                  />
               )}
               {canResetData && (
                  <MenuItem
                     key="reset-data"
                     label={t('resetData.clearData')}
                     onClick={onResetDataClick}
                  />
               )}
               <BrowserView>
                  {canControlMedia && (
                     <MenuItem
                        key="replace-media"
                        label={t('replaceMedia.action')}
                        onClick={onReplaceMediaClick}
                     />
                  )}
               </BrowserView>
            </>
         </TestablePopoverButton>
         {showCopyModal && (
            <CopyMediaModal
               onClose={onCopyClose}
               selectedMediaUrl={Url}
            />
         )}
         {showDeleteModal && (
            <DeleteModal
               onClose={onDeleteClose}
               onDelete={navigateToLibrary}
               mediaToDelete={[{Hash, IsVideo, Title} as IMediaDetails]}
               title={t('deleteMedia.title')}
               warning={config.featureSwitches.RecycleBin ? t('trash.softDelete.media.warning', {softDeletedMediaRetentionDays: config.softDeletedMediaRetentionDays}) : t('deleteMedia.warning')}
               testId="delete-media-modal"
            />
         )}
         {showResetDataModal && (
            <ResetDataModal
               onClose={onResetDataClose}
               onResetData={reloadPage}
               selectedMedia={selectedMedia}
            />
         )}
         {showSetThumbnailModal && <SetThumbnailModal onClose={onSetThumbnailClose} />}
         <BrowserView>
            {showReplaceMedialModal && <ReplaceMediaModal onClose={onReplaceMediaClose} />}
         </BrowserView>
      </MoreMenuWrapper>
   );
};

export interface IStateMappedProps {
   Hash: string;
   IsYouTube: boolean;
   DownloadUrl: string;
   DownloadEnabled: boolean;
   IsReady: boolean;
   IsVideo: boolean;
   Url: string;
   Title: string;
}

const mapStatesToProps = (
   // eslint-disable-next-line no-empty-pattern
   {},
   {Hash, IsYouTube, DownloadUrl, DownloadEnabled, IsReady, IsVideo, Url, Title}: Partial<IStateMappedProps>
): IStateMappedProps => ({Hash, IsYouTube, DownloadUrl, DownloadEnabled, IsReady, IsVideo, Url, Title});

export default withMemoizedContexts(mapStatesToProps, mediaStore)(MoreMenuBase);
